import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from './AuthContext';

const ChatContext = createContext();

const POLL_INTERVAL_MS = 30000;   // check every 30 seconds
const IDLE_TIMEOUT_MS  = 5 * 60 * 1000; // sleep after 5 minutes of no activity

export const useChatContext = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChatContext must be used within ChatContextProvider');
  }
  return context;
};

export const ChatContextProvider = ({ children }) => {
  const { user } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);

  const pollingRef  = useRef(null);  // holds the setInterval id
  const idleRef     = useRef(null);  // holds the idle setTimeout id
  const isIdleRef   = useRef(false); // true when user is considered idle

  function isTokenExpired(token) {
    try {
      const [, payload] = token.split('.');
      const { exp } = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
      return exp < Math.floor(Date.now() / 1000);
    } catch {
      return true;
    }
  }

  const fetchUnreadCount = useCallback(async () => {
    const token = localStorage.getItem('rv_access_token') || localStorage.getItem('access_token');

    // Skip if no user, no token, or token is expired
    if (!user || !token || isTokenExpired(token)) {
      setUnreadCount(0);
      return;
    }

    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';
      const response = await fetch(`${API_BASE}/api/chats/unread-count`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setUnreadCount(data.unread_count || 0);
      } else {
        setUnreadCount(0);
      }
    } catch (error) {
      console.error('Failed to fetch unread count:', error);
      setUnreadCount(0);
    }
  }, [user]);

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  const startPolling = useCallback(() => {
    stopPolling();
    pollingRef.current = setInterval(fetchUnreadCount, POLL_INTERVAL_MS);
  }, [fetchUnreadCount, stopPolling]);

  // Called on any user activity — resets the 5-min idle timer
  const onActivity = useCallback(() => {
    // Reset idle countdown
    if (idleRef.current) clearTimeout(idleRef.current);

    // If we were idle, wake up — fetch immediately + resume polling
    if (isIdleRef.current) {
      console.log('[CHAT] User returned — resuming notification polling');
      isIdleRef.current = false;
      fetchUnreadCount();
      startPolling();
    }

    // Schedule next sleep
    idleRef.current = setTimeout(() => {
      console.log('[CHAT] User idle — pausing notification polling');
      isIdleRef.current = true;
      stopPolling();
    }, IDLE_TIMEOUT_MS);
  }, [fetchUnreadCount, startPolling, stopPolling]);

  // Attach/detach activity listeners and start/stop polling with user session
  useEffect(() => {
    if (!user) {
      stopPolling();
      if (idleRef.current) clearTimeout(idleRef.current);
      setUnreadCount(0);
      return;
    }

    const events = ['mousemove', 'keydown', 'click', 'touchstart', 'scroll'];
    events.forEach(e => window.addEventListener(e, onActivity, { passive: true }));

    // Initial fetch + start polling + start idle timer
    setTimeout(fetchUnreadCount, 2000);
    startPolling();
    idleRef.current = setTimeout(() => {
      console.log('[CHAT] User idle — pausing notification polling');
      isIdleRef.current = true;
      stopPolling();
    }, IDLE_TIMEOUT_MS);

    return () => {
      events.forEach(e => window.removeEventListener(e, onActivity));
      stopPolling();
      if (idleRef.current) clearTimeout(idleRef.current);
    };
  }, [user]);

  const resetUnreadCount = () => setUnreadCount(0);

  return (
    <ChatContext.Provider value={{ unreadCount, fetchUnreadCount, resetUnreadCount }}>
      {children}
    </ChatContext.Provider>
  );
};